/**
 * ShopNotificationBell — header bell for SHOP-side in-app notifications
 * (subscription_reminder + future types). Separate from the admin
 * `NotificationBell.jsx` which targets /api/admin/notifications.
 *
 * Polls /api/notifications/me every 60 s and renders an unread dot + dropdown.
 */
import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { Bell, MessageCircle, ArrowRight, CheckCheck } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const sec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

export default function ShopNotificationBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const containerRef = useRef(null);

  const load = () =>
    api.get("/notifications/me?limit=20")
      .then((r) => setData(r.data))
      .catch(() => {});

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!open) return;
    const fn = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [open]);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  const markOne = async (nid) => {
    await api.post(`/notifications/${nid}/read`);
    load();
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex items-center justify-center w-10 h-10 rounded-md hover:bg-slate-100 transition"
        aria-label="Notifications"
        data-testid="shop-notification-bell"
      >
        <Bell className="w-5 h-5 text-slate-700" />
        {data.unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center tabular"
            data-testid="shop-notif-unread-count"
          >
            {data.unread > 99 ? "99+" : data.unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-[320px] sm:w-[380px] bg-white border border-slate-200 rounded-md shadow-xl z-50"
          data-testid="shop-notif-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
            <div className="font-semibold text-sm">Notifications</div>
            {data.unread > 0 && (
              <button
                onClick={markAll}
                className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1"
                data-testid="shop-notif-mark-all"
              >
                <CheckCheck className="w-3.5 h-3.5" /> Mark all read
              </button>
            )}
          </div>
          <div className="overflow-y-auto max-h-[60vh] divide-y divide-slate-100">
            {data.items.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-slate-500" data-testid="shop-notif-empty">
                Koi nayi notification nahi.
              </div>
            )}
            {data.items.map((n) => (
              <div
                key={n.id}
                className={`p-3 ${n.read ? "bg-white" : "bg-amber-50/40"}`}
                data-testid={`shop-notif-item-${n.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="font-semibold text-sm leading-snug flex-1">{n.title}</div>
                  <span className="text-[10px] text-slate-400 tabular shrink-0">{timeAgo(n.created_at)}</span>
                </div>
                {n.body && (
                  <div className="text-xs text-slate-600 mt-1 whitespace-pre-wrap line-clamp-4">{n.body}</div>
                )}
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {n.cta_url && (
                    <Link
                      to={n.cta_url}
                      onClick={() => { markOne(n.id); setOpen(false); }}
                      className="inline-flex items-center gap-1 text-xs font-semibold rounded-md bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1.5"
                      data-testid={`shop-notif-cta-${n.id}`}
                    >
                      {n.cta_label || "Open"} <ArrowRight className="w-3 h-3" />
                    </Link>
                  )}
                  {n.wa_link && (
                    <a
                      href={n.wa_link}
                      target="_blank" rel="noreferrer"
                      onClick={() => markOne(n.id)}
                      className="inline-flex items-center gap-1 text-xs font-semibold rounded-md border border-emerald-200 text-emerald-700 hover:bg-emerald-50 px-2.5 py-1.5"
                      data-testid={`shop-notif-wa-${n.id}`}
                    >
                      <MessageCircle className="w-3 h-3" /> WhatsApp
                    </a>
                  )}
                  {!n.read && (
                    <button
                      onClick={() => markOne(n.id)}
                      className="ml-auto text-xs text-slate-500 hover:text-slate-700"
                    >
                      Mark read
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
